// Prompt-bar readout for a selection that has to cover a cost (interactions.json `tally`): what the cost asks for, as published in
// a numeric observed counter, and what the selected objects supply, as the sum of their numeric `<fieldPrefix><unit>` fields.
// Display only. It never decides whether a selection pays: the package's own Query check and Apply do.

import type { TableModel } from '../model/table';
import type { ChoiceStep, Scalar } from '../protocol/ui';
import type { TallyRule } from '../presentation/types';

export interface Tally {
  rule: TallyRule;
  /** What the cost asks for, when the observation states it. */
  needed: number | null;
  /** Units the selection supplies, in the rule's unit order (zero units omitted). */
  units: { unit: string; count: number }[];
  total: number;
}

export function tallyRuleFor(rules: TallyRule[] | undefined, group: string): TallyRule | null {
  return rules?.find(r => group.startsWith(r.group)) ?? null;
}

const num = (value: Scalar | undefined): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/** The option objects behind the selected values of the draft's current pick (options without a table object add nothing). */
export function selectedObjectIds(step: ChoiceStep | null, selected: Scalar[]): string[] {
  if (!step || step.type !== 'pick' || step.expand) return [];
  const byValue = new Map(step.options.map(o => [o.value, o.objectId]));
  return selected.flatMap(v => { const id = byValue.get(v); return id ? [id] : []; });
}

export function tallyOf(rule: TallyRule, table: Pick<TableModel, 'counters' | 'objects' | 'selfSeat'>, objectIds: string[]): Tally {
  const counter = table.counters.find(c => c.id === rule.counter && (c.owner === null || c.owner === table.selfSeat));
  const needed = counter && typeof counter.value === 'number' ? counter.value : counter && typeof counter.value === 'string' && /^\d+$/.test(counter.value) ? Number(counter.value) : null;
  const units = rule.units.map(unit => ({ unit, count: objectIds.reduce((sum, id) => sum + num(table.objects.get(id)?.fields[rule.fieldPrefix + unit]), 0) })).filter(u => u.count > 0);
  return { rule, needed, units, total: units.reduce((sum, u) => sum + u.count, 0) };
}
